import { ImageField, RouteData, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { decode } from 'html-entities';
import { DateTime } from 'luxon';
import Head from 'next/head';
import Script from 'next/script';
import React from 'react';
// generated
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { Data } from '.generated/templates/Feature.Data.model';
import { NetworkResourceCenter } from '.generated/templates/Project.NetworkResourceCenter.model';
import { NmdpSite } from '.generated/templates/Project.NmdpSite.constants';
import { Routes as Dataconstants } from '.generated/templates/Project.Routes.constants';
import { Routes } from '.generated/templates/Project.Routes.model';
// lib
import { normalizeGuid } from 'lib/sitecore/normalize-guid';
import { getEnumValue } from 'lib/util/enum';
import { getLocaleSlug } from 'lib/util/locale';
import { getAbsolutePath } from 'lib/util/urls';
// local
import { Item, LayoutServiceContext } from '@sitecore-jss/sitecore-jss-react';
import { logWarning } from 'lib/util/errors';
import { mapMetadataElements, MetadataElement } from './MetadataElement';
import { MetadataItem } from './types';
import { isMetadataWithName, makeMetadataKey, mapFromObject } from './utils';
import { IsRITN } from 'lib/util/site-configuration';

export { makeMetadataKey, mapFromObject, mapMetadataElements, MetadataElement };
export type { MetadataItem };

type audience = Sitecore.Override.ItemEx &
  NetworkResourceCenter.Announcements.Fields.Audience & {
    fields: {
      centerType: NetworkResourceCenter.Announcements.Fields.CenterType;
    };
  };

const emptyDateString = '0001-01-01T00:00:00Z';
const KnownMetaNames = {
  keywords: 'keywords',
  title: 'title',
  description: 'description',
  date: 'DC.DATE',
  audience: 'nmdp-centers',
  nmdpAudience: 'nmdp:audiences',
  format: 'nmdp:format',
  searchableCategories: 'nmdp:searchable-categories',
  topics: 'nmdp:topics',
  disease: 'nmdp:disease',
};
const normalizeStringValue = (s?: string) => {
  const ret = s?.trim();
  return ret?.length ? ret : undefined;
};
const isEmptyDateValue = (v?: string) => !normalizeStringValue(v) || v === emptyDateString;
const isNotEmptyDateValue = (v?: string): v is string => !isEmptyDateValue(v);
const getEventStartAndEndDate = (page: Routes.EventPage) => {
  const occursAnnually = page.fields?.occursAnnually.value;
  let eventExpired = false;
  let currentYearExpired = false;
  let startDate = page.fields?.startDateTime?.value as string;
  let endDate = page.fields?.endDateTime?.value as string;
  if (occursAnnually) {
    const todayDate = DateTime.now();
    const startDateTime = DateTime.fromISO(startDate);
    const endDateTime = DateTime.fromISO(endDate);
    if (startDateTime < todayDate && endDateTime < todayDate) {
      eventExpired = true;
      let startDateField = startDate;
      let endDateField = endDate;
      const year = todayDate.year;
      const startYear = Number(startDateField.slice(0, 4));
      const endYear = Number(endDateField.slice(0, 4));
      startDateField = year.toString().concat(startDateField.slice(4));
      endDateField = (year + (endYear - startYear)).toString().concat(endDateField.slice(4));
      const currentStartDateTime = DateTime.fromISO(startDateField);
      const currentEndDateTime = DateTime.fromISO(endDateField);
      if (currentStartDateTime < todayDate && currentEndDateTime < todayDate) {
        currentYearExpired = true;
      }
    }
    if (eventExpired) {
      let startDateYear = Number(startDate.slice(0, 4));
      let endDateYear = Number(endDate.slice(0, 4));
      if (currentYearExpired) {
        endDateYear = todayDate.year + (endDateYear - startDateYear) + 1;
        startDateYear = todayDate.year + 1;
      } else {
        endDateYear = todayDate.year + (endDateYear - startDateYear);
        startDateYear = todayDate.year;
      }
      startDate = startDateYear.toString().concat(startDate.slice(4));
      endDate = endDateYear.toString().concat(endDate.slice(4));
    }
  }
  return {
    startDate: normalizeStringValue(startDate),
    endDate: normalizeStringValue(endDate),
  };
};
const buildStringFromItems = <T extends Item>({
  items,
  format,
  join,
  unique = false,
}: {
  items?: T[];
  format: (x: T) => string;
  join?: string;
  unique?: boolean;
}) => {
  let mappedItems = (items ?? []).map(format);
  if (unique) {
    mappedItems = [...new Set<string>(mappedItems)];
  }
  const ret = mappedItems.join(join ?? ', ').trim();
  return ret.length ? ret : undefined;
};
const getRobotsMeta = (basePageData?: Routes.GenericPage): MetadataItem[] => {
  const robotsData = buildStringFromItems({
    items: basePageData?.fields?.metaRobots,
    format: (u) => getEnumValue(u),
  });
  return robotsData ? [{ name: 'robots', content: robotsData }] : [];
};
const getPageType = (route: RouteData) => {
  switch (normalizeGuid(route.templateId)) {
    case normalizeGuid(Dataconstants.TemplateIds.ArticlePage):
      return 'article';
    case normalizeGuid(Dataconstants.TemplateIds.EventPage):
      return 'event';
    case normalizeGuid(Dataconstants.TemplateIds.Announcement):
      return 'announcement';
    case normalizeGuid(Dataconstants.TemplateIds.AnnouncementV2):
      return 'announcement';
    case normalizeGuid(Dataconstants.TemplateIds.EducationCatalogPage):
      return 'education';
    case normalizeGuid(Dataconstants.TemplateIds.ProductDetailsPage):
      return 'productlist';
    case normalizeGuid(NmdpSite.TemplateIds.Listing):
      return 'tclisting';
    default:
      return 'other';
  }
};
const getArticlePageData = (page: Routes.ArticlePage): MetadataItem[] => {
  if (!page?.fields) {
    return [];
  }
  const author = page.fields.author as unknown as Data.Authors.Fields.Author | undefined;
  let publishDate: string | undefined;
  if (!isEmptyDateValue(page.fields.publishedDate?.value)) {
    publishDate = DateTime.fromISO(page.fields.publishedDate?.value)
      .toFormat('yyyy-MM-dd')
      .toString();
  }
  const topics = buildStringFromItems({
    items: page.fields.topics,
    format: getEnumValue,
  });
  return [
    { name: KnownMetaNames.date, content: publishDate },
    ...(author
      ? [
          {
            name: 'nmdp:article:author',
            content: `${author.fields?.firstName?.value} ${author.fields?.lastName?.value}`,
          },
        ]
      : []),
    ...(topics ? [{ name: KnownMetaNames.topics, content: topics }] : []),
  ];
};
const getEventListingPageData = (page: Routes.EventPage): MetadataItem[] => {
  if (!page?.fields) {
    return [];
  }
  const { startDate, endDate } = getEventStartAndEndDate(page);
  const eventCategory = buildStringFromItems({
    items: page.fields.eventCategory as (Data.Events.Fields.EventCategory &
      Sitecore.Override.ItemEx)[],
    format: (x) => x.fields?.eventCategoryTitle?.value,
  });
  const location = normalizeStringValue(page.fields?.location?.value);
  return [
    ...(eventCategory ? [{ name: 'nmdp:event:category', content: eventCategory }] : []),
    ...(location ? [{ name: 'nmdp:event:location', content: location }] : []),
    ...(startDate ? [{ name: 'nmdp:event:startDate', content: startDate }] : []),
    ...(endDate ? [{ name: 'nmdp:event:endDate', content: endDate }] : []),
  ];
};
const getAnnouncementPageData = (page: Routes.Announcement): MetadataItem[] => {
  if (!page?.fields) {
    return [];
  }
  const centerlist = buildStringFromItems<audience>({
    items: page.fields.audiences as audience[],
    format: (a) => a.fields.centerType?.fields?.centerTypeName?.value,
    join: '; ',
    unique: true,
  });
  let publishDate: string | undefined;
  if (isNotEmptyDateValue(page.fields.PublishDate?.value)) {
    publishDate = page.fields.PublishDate.value;
  }
  const keywords = normalizeStringValue(page.fields.keywords?.value);
  const summary = normalizeStringValue(page.fields.summary?.value);
  const title = normalizeStringValue(page.fields.subject?.value);

  return [
    ...(keywords ? [{ name: KnownMetaNames.keywords, content: keywords }] : []),
    ...(summary ? [{ name: KnownMetaNames.description, content: decode(summary) }] : []),
    ...(publishDate ? [{ name: KnownMetaNames.date, content: publishDate }] : []),
    ...(title ? [{ name: KnownMetaNames.title, content: title }] : []),
    ...(centerlist ? [{ name: KnownMetaNames.audience, content: centerlist }] : []),
  ];
};
const getAnnouncementV2PageData = (page: Routes.AnnouncementV2): MetadataItem[] => {
  if (!page?.fields) {
    return [];
  }

  const centerlist = buildStringFromItems<audience>({
    items: [
      ...((page.fields['domestic audiences'] as audience[]) ?? []),
      ...((page.fields['international audiences'] as audience[]) ?? []),
    ],
    format: (a) => a.fields.centerType?.fields?.centerTypeName?.value,
    join: '; ',
    unique: true,
  });

  let publishDate: string | undefined;
  if (isNotEmptyDateValue(page.fields.PublishDate?.value)) {
    publishDate = page.fields.PublishDate.value;
  }
  const keywords = normalizeStringValue(page.fields.keywords?.value);
  const summary = normalizeStringValue(page.fields.summary?.value);
  const title = normalizeStringValue(page.fields.subject?.value);

  return [
    ...(keywords ? [{ name: KnownMetaNames.keywords, content: keywords }] : []),
    ...(summary ? [{ name: KnownMetaNames.description, content: decode(summary) }] : []),
    ...(publishDate ? [{ name: KnownMetaNames.date, content: publishDate }] : []),
    ...(title ? [{ name: KnownMetaNames.title, content: title }] : []),
    ...(centerlist ? [{ name: KnownMetaNames.audience, content: centerlist }] : []),
  ];
};
const getEducationPageData = (page: Routes.EducationCatalogPage): MetadataItem[] => {
  if (!page?.fields) {
    return [];
  }
  const audiences = buildStringFromItems({
    items: page.fields.audiences,
    format: getEnumValue,
  });
  const formats = buildStringFromItems({
    items: page.fields.formats,
    format: getEnumValue,
  });
  const keywords = normalizeStringValue(page.fields.metaKeyWords?.value);
  const description = normalizeStringValue(page.fields.metaDescription?.value);
  const title = normalizeStringValue(page.fields.pageTitle?.value);
  return [
    ...(keywords ? [{ name: KnownMetaNames.keywords, content: keywords }] : []),
    ...(description ? [{ name: KnownMetaNames.description, content: description }] : []),
    ...(title ? [{ name: KnownMetaNames.title, content: title }] : []),
    ...(audiences ? [{ name: KnownMetaNames.audience, content: audiences }] : []),
    ...(formats ? [{ name: KnownMetaNames.format, content: formats }] : []),
  ];
};
const getProductPageData = (page: Routes.ProductDetailsPage): MetadataItem[] => {
  if (!page?.fields) {
    return [];
  }

  const mediaType = buildStringFromItems({
    items: page.fields.mediaType,
    format: getEnumValue,
  });
  const clinicalTopics = buildStringFromItems({
    items: page.fields.clinicalTopics,
    format: getEnumValue,
  });
  const operationalTopics = buildStringFromItems({
    items: page.fields.operationalTopics,
    format: getEnumValue,
  });
  const patientResources = buildStringFromItems({
    items: page.fields.patientResources,
    format: getEnumValue,
  });
  const productLanguages = buildStringFromItems({
    items: page.fields.productLanguages,
    format: getEnumValue,
  });
  const productImage = page.fields.productImage?.length
    ? page.fields.productImage[0].url
    : undefined;
  const productTitle = normalizeStringValue(page.fields.productTitle?.value);
  const productNumber = normalizeStringValue(page.fields.productNumber?.value);
  const resourceType = buildStringFromItems({
    items: page.fields.resourceType,
    format: getEnumValue,
  });
  return [
    ...(productTitle ? [{ name: 'productTitle', content: productTitle }] : []),
    ...(productNumber ? [{ name: 'productNumber', content: productNumber }] : []),
    ...(productImage ? [{ name: 'productImages', content: productImage }] : []),
    ...(resourceType
      ? [{ name: 'nmdp:cat:catalog-filters:resource-type', content: resourceType }]
      : []),
    ...(mediaType ? [{ name: 'nmdp:cat:catalog-filters:media-type', content: mediaType }] : []),
    ...(clinicalTopics
      ? [
          {
            name: 'nmdp:cat:catalog-filters:clinical-topic',
            content: clinicalTopics,
          },
        ]
      : []),
    ...(operationalTopics
      ? [
          {
            name: 'nmdp:cat:catalog-filters:operational-topic',
            content: operationalTopics,
          },
        ]
      : []),
    ...(patientResources
      ? [
          {
            name: 'nmdp:cat:catalog-filters:patient-resources',
            content: patientResources,
          },
        ]
      : []),
    ...(productLanguages
      ? [
          {
            name: 'nmdp:cat:catalog-filters:language',
            content: productLanguages,
          },
        ]
      : []),
  ];
};
const getGenericPageData = (page: Routes.GenericPage): MetadataItem[] => {
  if (!page?.fields) {
    return [];
  }
  const searchCategories = buildStringFromItems({
    items: page.fields.searchCategories,
    format: getEnumValue,
  });
  const audiences = buildStringFromItems({
    items: page.fields.audiences,
    format: getEnumValue,
  });
  const formats = buildStringFromItems({
    items: page.fields.formats,
    format: getEnumValue,
  });
  const topics = buildStringFromItems({
    items: page.fields.topics,
    format: getEnumValue,
  });
  const disease = buildStringFromItems({
    items: page.fields.disease,
    format: getEnumValue,
  });

  return [
    ...(searchCategories
      ? [{ name: KnownMetaNames.searchableCategories, content: searchCategories }]
      : []),
    ...(searchCategories && formats ? [{ name: KnownMetaNames.format, content: formats }] : []),
    ...(searchCategories && audiences
      ? [{ name: KnownMetaNames.nmdpAudience, content: audiences }]
      : []),
    ...(searchCategories && topics ? [{ name: KnownMetaNames.topics, content: topics }] : []),
    ...(searchCategories && disease ? [{ name: KnownMetaNames.disease, content: disease }] : []),
  ];
};
const getPageSpecificMeta = (route: RouteData): MetadataItem[] => {
  switch (normalizeGuid(route.templateId)) {
    case normalizeGuid(Dataconstants.TemplateIds.ArticlePage): {
      return getArticlePageData(route as unknown as Routes.ArticlePage);
    }
    case normalizeGuid(Dataconstants.TemplateIds.EventPage): {
      return getEventListingPageData(route as unknown as Routes.EventPage);
    }
    case normalizeGuid(Dataconstants.TemplateIds.Announcement): {
      return getAnnouncementPageData(route as unknown as Routes.Announcement);
    }
    case normalizeGuid(Dataconstants.TemplateIds.AnnouncementV2): {
      return getAnnouncementV2PageData(route as unknown as Routes.AnnouncementV2);
    }
    case normalizeGuid(Dataconstants.TemplateIds.EducationCatalogPage): {
      return getEducationPageData(route as unknown as Routes.EducationCatalogPage);
    }
    case normalizeGuid(Dataconstants.TemplateIds.ProductDetailsPage): {
      return getProductPageData(route as unknown as Routes.ProductDetailsPage);
    }
    default: {
      return getGenericPageData(route as unknown as Routes.GenericPage);
    }
  }
};
const getAdditionalMeta = (route: RouteData): MetadataItem[] => {
  const page = route as unknown as Routes.GenericPage;
  const additionalData = normalizeStringValue(page?.fields?.additionalMetaTags?.value);
  return additionalData ? mapFromObject(new URLSearchParams(additionalData)) : [];
};

export const getPageTitle = (
  basePageData: Routes.GenericPage,
  templateId: string | undefined
): string => {
  const sufix = '| NMDP';
  const pageTitle =
    basePageData.fields?.metaTitle?.value ||
    basePageData?.fields?.pageTitle?.value ||
    'NMDP : BeConnected';

  if (
    templateId === Dataconstants.TemplateIds.HomePage ||
    pageTitle.toUpperCase().includes(sufix)
  ) {
    return pageTitle;
  }

  return `${pageTitle} ${sufix}`;
};

/**
 * Reduces {@link LayoutServiceContext} down to the properties needed to generate a canonical URL.
 */
type GetCanonicalUrlProps = Pick<LayoutServiceContext, 'itemPath'> & {
  route?: Pick<RouteData, 'itemLanguage'>;
};

/**
 *
 * @param {GetCanonicalUrlProps} props {@linkcode GetCanonicalUrlProps} property object
 * @returns
 */
export const getCanonicalUrl = ({
  itemPath: pathFromProps = '',
  route: { itemLanguage = '' } = {},
}: GetCanonicalUrlProps = {}): string | undefined => {
  const itemPath = pathFromProps.trim().toLocaleLowerCase();
  if (!itemPath.length) {
    logWarning('getCanonicalUrl: itemPath is empty or undefined');
    return undefined;
  }
  return getAbsolutePath(`${getLocaleSlug(itemLanguage.length ? itemLanguage : 'en')}${itemPath}`);
};

const HeadMeta = (): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const { route } = sitecoreContext ?? {};
  if (!route) {
    return <></>;
  }
  const featuredImage = sitecoreContext.defaultFeaturedImage as ImageField;
  const basePageData = route as Routes.GenericPage;

  const getLastUpdatedDate = () => {
    let returnDate = '';
    if (basePageData.fields?.lastUpdated?.value !== emptyDateString) {
      returnDate = basePageData.fields?.lastUpdated?.value as string;
    }
    return returnDate;
  };
  const pageMeta = {
    pageTitle: getPageTitle(basePageData, route.templateId),
    pageDescription:
      basePageData.fields?.metaDescription?.value ||
      basePageData?.fields?.pageDescription?.value ||
      '',
    metaImage: String(
      getAbsolutePath(
        basePageData.fields?.metaImage?.value?.src ||
          basePageData.fields?.pageFeaturedImage?.value?.src ||
          featuredImage.value?.src ||
          ''
      )
    ),
    url: getCanonicalUrl(sitecoreContext),
    pageType: route.templateName !== 'Article Page' ? 'website' : 'article',
    nmdpPageType: getPageType(route),
    pageUpdatedDate: getLastUpdatedDate(),
    metaKeywords: basePageData?.fields?.metaKeyWords?.value,
    pageID: basePageData?.itemId || '',
  };

  const staticPath = getAbsolutePath('static');
  const robotsMeta = getRobotsMeta(basePageData);

  const pageMetaItems = [
    { name: 'nmdp:type', content: pageMeta.nmdpPageType },
    { name: 'nmdp:page:updatedDate', content: pageMeta.pageUpdatedDate },
    { name: 'nmdp:pageId', content: pageMeta.pageID },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:image', content: pageMeta.metaImage },
    { property: 'og:title', content: pageMeta.pageTitle },
    { property: 'og:description', content: pageMeta.pageDescription },
    { property: 'og:image', content: pageMeta.metaImage },
    { name: 'og:url', content: pageMeta.url },
    { name: 'og:type', content: pageMeta.pageType },
    ...robotsMeta,
    ...getAdditionalMeta(route),
    ...getPageSpecificMeta(route),
  ];
  if (
    !pageMetaItems.find((mi) => isMetadataWithName(mi) && mi.name === KnownMetaNames.description)
  ) {
    pageMetaItems.push({ name: KnownMetaNames.description, content: pageMeta.pageDescription });
  }
  if (
    !pageMetaItems.find((mi) => isMetadataWithName(mi) && mi.name === KnownMetaNames.description)
  ) {
    pageMetaItems.push({ name: KnownMetaNames.keywords, content: pageMeta.metaKeywords });
  }
  /*
  react/no-invalid-html-attribute disabled on a number of lines to support apple
  proprietary attributes
  */
  return (
    <>
      <Head key="nmdp-head-meta">
        <title key="title">{pageMeta.pageTitle}</title>
        <link rel="icon" href={`${staticPath}/favicon.ico`} key="l-icon" />
        <link rel="canonical" key="l-canonical" href={pageMeta.url} />
        {mapMetadataElements({ items: pageMetaItems })}
      </Head>
      {IsRITN() ? (
        <Head>
          <link
            // eslint-disable-next-line react/no-invalid-html-attribute
            rel="apple-touch-icon-precomposed"
            key="l-apple-touch-icon-precomposed-57"
            sizes="57x57"
            href={`${staticPath}/ritn/ritn-apple-touch-icon-57x57-precomposed.png`}
          />
          <link
            // eslint-disable-next-line react/no-invalid-html-attribute
            rel="apple-touch-icon-precomposed"
            key="l-apple-touch-icon-precomposed-72"
            sizes="72x72"
            href={`${staticPath}/ritn/ritn-apple-touch-icon-72x72-precomposed.png`}
          />
          <link
            // eslint-disable-next-line react/no-invalid-html-attribute
            rel="apple-touch-icon-precomposed"
            key="l-apple-touch-icon-precomposed-114"
            sizes="114x114"
            href={`${staticPath}/ritn/ritn-apple-touch-icon-114x114-precomposed.png`}
          />
        </Head>
      ) : (
        <Head>
          <link
            key="l-apple-touch-icon-precomposed-57"
            // eslint-disable-next-line react/no-invalid-html-attribute
            rel="apple-touch-icon-precomposed"
            sizes="57x57"
            href={`${staticPath}/apple-touch-icon-57x57-precomposed.png`}
          />
          <link
            // eslint-disable-next-line react/no-invalid-html-attribute
            rel="apple-touch-icon-precomposed"
            key="l-apple-touch-icon-precomposed-72"
            sizes="72x72"
            href={`${staticPath}/apple-touch-icon-72x72-precomposed.png`}
          />
          <link
            // eslint-disable-next-line react/no-invalid-html-attribute
            rel="apple-touch-icon-precomposed"
            key="l-apple-touch-icon-precomposed-114"
            sizes="114x114"
            href={`${staticPath}/apple-touch-icon-114x114-precomposed.png`}
          />
        </Head>
      )}
      <Script id="nmdp-cdp-initq" strategy="afterInteractive">
        var _boxeverq=_boxeverq||[];
      </Script>
    </>
  );
};

export default HeadMeta;
